import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import theme from "../../../styles/theme.semantic";

const Indent = ({ children, className }) => {
  return (
    <div className={`row no-gutters ${className}`}>
      <div className="col-auto">
        <span className="indent">·</span>
      </div>
      <div className="col">{children}</div>
    </div>
  );
};

Indent.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string
};

const IndentWithStyles = styled(Indent)`
  /*indent for a div with ·*/
  .indent {
    margin: 0 5px 0 ${props => (props.double ? "20px" : "10px")};
  }
`;

const Table = ({ children, className, width }) => {
  return (
    <div className={className}>
      <table width={width}>{children}</table>
    </div>
  );
};

Table.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  width: PropTypes.string
};

const TableWithStyles = styled(Table)`
  table {
    width: ${props => props.width};
    margin-top: ${props => props.marginTop};
    border-collapse: collapse;
    border-spacing: 0;
  }

  tbody {
    vertical-align: top;
  }

  caption {
    font-size: 28px;
    font-weight: 500;
    line-height: 1.4;
  }

  tbody td {
    padding-top: ${props => props.tableRowSpace};
    padding-bottom: ${props => props.tableRowSpace};
  }

  thead th {
    padding-top: ${props => props.tableHeaderMarginTop};
    padding-bottom: ${props => props.tableRowSpace};
  }

  th,
  td,
  caption {
    text-align: left;
  }

  tr th:first-child,
  tr td:first-child {
    word-break: break-word;
  }

  tr th:nth-child(2),
  tr td:nth-child(2) {
    width: ${props => props.col2Size};
    padding-right: 20px;
    padding-left: 20px;
  }

  tr th:nth-child(3),
  tr td:nth-child(3) {
    width: ${props => props.col3Size};
  }

  tr th:nth-child(4),
  tr td:nth-child(4) {
    width: ${props => props.col4Size};
  }

  // Small devices (landscape phones, 576px and up)
  @media (max-width: 576px) {
    tr th:nth-child(2),
    tr td:nth-child(2) {
      width: ${props => props.col2SizeMobile};
    }

    tr th:nth-child(3),
    tr td:nth-child(3) {
      width: ${props => props.col3SizeMobile};
    }

    tr th:nth-child(4),
    tr td:nth-child(4) {
      width: ${props => props.col4SizeMobile};
    }
  }

  tbody td {
    border-top: ${props =>
      props.borders
        ? `1px solid ${theme.components.table.borderColor}`
        : props.lightBorders
        ? `1px solid ${props.theme.components.table.borderLightColor}`
        : null};
    border-bottom: ${props =>
      props.borders
        ? `1px solid ${props.theme.components.table.borderColor}`
        : props.lightBorders
        ? `1px solid ${props.theme.components.table.borderLightColor}`
        : null};
  }

  /*An option has color*/
  .option {
    color: ${props => props.theme.components.table.optionColor};
  }

  /*An option inside minor has indent and color*/
  .option-inside-minor {
    margin-left: 10px;
  }

  /*A minor has color and weight*/
  .minor {
    font-weight: 400;
    color: ${props => props.theme.components.table.optionColor};
  }

  .last-child {
    padding-top: 20px;
    font-weight: 400;
  }

  .if-has-links {
    a {
      text-decoration: underline;
      text-decoration-color: ${props =>
        props.theme.components.table.linksTextDecorationColor};
    }
  }
`;

TableWithStyles.defaultProps = {
  width: "100%",
  tableRowSpace: "6px",
  marginTop: "40px",
  tableHeaderMarginTop: "20px"
};

export { TableWithStyles, IndentWithStyles };
